/*
DNCL と共通テスト用プログラム表記を Python で書いて動かす頁 (/joho1/dncl)。ログイン無しで開ける公開ページで、canonical は自分自身。
「情報 I」の束 (/joho1) の最初の頁。/joho1 の入口はまだ無いので、パンくずの「情報 I」はリンクにせず、構造化データのパンくずにも入れない。
中身と出典は dncl/content.ts。表の 1 行ずつと例題の出力は、大学入試センターの PDF と python3 で照らしてある (dncl.test.ts)
*/
import type { MetaFunction } from "react-router";
import { Link } from "react-router";
import { PublicPageShell } from "@/components/common/PublicPageShell";
import { PublicPageBand } from "@/components/page-band/PublicPageBand";
import { generateNoimanSEOMeta } from "@/lib/seo-config";
import { generateArticleSchema, generateBreadcrumbSchema } from "@/lib/structured-data";
import {
  BINARY_SEARCH,
  DRILLS,
  NEXT_LINKS,
  OPERATORS,
  PAGE_META,
  PAGE_URL,
  PITFALLS,
  ROWS,
  SOURCES,
} from "./dncl/content";
import { CorrespondenceTable, DrillCard, ExampleBlock, NotationCode, SectionTitle } from "./dncl/parts";

export const meta: MetaFunction = () => [
  ...generateNoimanSEOMeta({
    title: PAGE_META.seoTitle,
    description: PAGE_META.description,
    url: PAGE_URL,
    canonicalUrl: PAGE_URL,
    rawTitle: true,
    keywords: ["DNCL", "DNCL とは", "共通テスト用プログラム表記", "DNCL Python", "情報I", "共通テスト 情報"],
  }),
  {
    "script:ld+json": generateArticleSchema({
      title: PAGE_META.title,
      description: PAGE_META.description,
      publishedDate: PAGE_META.published,
      modifiedDate: PAGE_META.updated,
      author: PAGE_META.author,
      url: PAGE_URL,
    }),
  },
  {
    "script:ld+json": generateBreadcrumbSchema([
      { name: "ホーム", url: "https://yumesaku.tech" },
      { name: PAGE_META.crumb, url: PAGE_URL },
    ]),
  },
];

const jaDate = (d: string) => {
  const [y, m, day] = d.split("-").map(Number);
  return `${y}年${m}月${day}日`;
};

const TOC = [
  { id: "names", label: "DNCL と共通テスト用プログラム表記" },
  { id: "table", label: "3 つの表記の対応表" },
  { id: "run", label: "二分探索を Python で動かす" },
  { id: "drill", label: "書き換えを自分で試す" },
  { id: "pitfalls", label: "書き間違いと読み間違い" },
  { id: "sources", label: "出典と確かめた日" },
  { id: "next", label: "次に学ぶ" },
];

const P = "text-[17px] leading-[1.9] text-main [overflow-wrap:break-word]";

export default function DnclPage() {
  return (
    <PublicPageShell>
      <div className="bg-main text-main" data-joho1-page="dncl">
        <PublicPageBand title={PAGE_META.title} crumbs={[{ label: "情報 I" }]} column={{ max: 1080, pad: 40 }} />
        <header className="border-b border-main bg-surface">
          <div className="mx-auto w-full max-w-[1080px] px-4 pb-6 pt-5 sm:px-5 md:px-10 md:pb-8 md:pt-7">
            <p className="m-0 max-w-[820px] text-[17px] leading-[1.9] text-main [text-wrap:pretty]">{PAGE_META.description}</p>
            <p className="mt-3 flex flex-wrap gap-x-4 text-[15px] text-sub">
              <span>{`文 ${PAGE_META.author}`}</span>
              <span className="whitespace-nowrap">{`公開 ${jaDate(PAGE_META.published)}`}</span>
              <span className="whitespace-nowrap">{`更新 ${jaDate(PAGE_META.updated)}`}</span>
            </p>
          </div>
        </header>

        <div className="mx-auto w-full max-w-[1080px] px-4 py-8 sm:px-5 md:px-10 md:py-10">
          <article className="min-w-0">
            <div className="rounded-md border-2 border-main bg-surface p-5 md:p-6">
              <p className="m-0 text-[15px] font-bold text-sub">先に答え</p>
              <ul className="mt-2 list-disc space-y-2 pl-5 text-[17px] leading-[1.8]">
                <li>
                  DNCL は「共通テスト手順記述標準言語」で、科目「情報関係基礎」のための表記です。代入は <code className="font-mono">←</code> で書きます。
                </li>
                <li>
                  情報 I の共通テストが使うのは「共通テスト用プログラム表記」です。代入は <code className="font-mono">=</code>、演算子は{" "}
                  <code className="font-mono">and</code> や <code className="font-mono">**</code> のように Python に近い形です。
                </li>
                <li>Python へは、ほぼ 1 行ずつ写せます。間違えやすいのは ÷ と ％、「まで」が終わりを含むこと、添字の始まりです。</li>
              </ul>
            </div>

            <nav aria-label="この頁の目次" className="mt-8">
              <p className="m-0 text-[15px] font-bold text-sub">目次</p>
              <ol className="mt-2 grid list-decimal gap-x-8 gap-y-1 pl-6 text-[17px] leading-[1.8] md:grid-cols-2">
                {TOC.map((t) => (
                  <li key={t.id}>
                    <a href={`#${t.id}`} className="text-primary underline underline-offset-4">
                      {t.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className="mt-12">
              <SectionTitle id="names">DNCL と共通テスト用プログラム表記</SectionTitle>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-md border border-main bg-surface p-5">
                  <h3 className="m-0 text-[19px] font-bold">旧 DNCL</h3>
                  <p className={`mt-2 ${P}`}>
                    大学入試センターの「共通テスト手順記述標準言語 (DNCL) の説明」(2022 年 1 月版) が定める表記です。科目「情報関係基礎」の出題に使うと書かれています。代入は ←、かけ算は ×、比べる記号は ＝ と ≠ です。
                  </p>
                </div>
                <div className="rounded-md border border-main bg-surface p-5">
                  <h3 className="m-0 text-[19px] font-bold">共通テスト用プログラム表記</h3>
                  <p className={`mt-2 ${P}`}>
                    「情報 I」の共通テストのための表記です。大学入試センターは「概要『情報』」の中に、その例示を載せています (PDF の更新日は 2022 年 11 月 9 日)。代入は =、比べる記号は == と != です。
                  </p>
                </div>
              </div>
              <p className={`mt-4 ${P}`}>
                検索では両方とも「DNCL」と呼ばれがちですが、別の物です。情報 I の対策で ← を使う書き方を覚えると、試験の問題と記号が合いません。どちらの説明書も、問題文を簡潔にするためなどで説明と違う形で出題することがあると断っていて、試験では問題文の説明や指示が優先します。
              </p>
            </div>

            <div className="mt-12">
              <SectionTitle id="table">3 つの表記の対応表</SectionTitle>
              <p className={`mb-4 ${P}`}>
                旧 DNCL は 2022 年 1 月版の説明書、情報 I は 2022 年 11 月 9 日の例示から取りました。片方の説明書に載っていない物は「無い」と書いています。
              </p>
              <CorrespondenceTable rows={ROWS} />
            </div>

            <div className="mt-12">
              <SectionTitle id="run">二分探索を Python で動かす</SectionTitle>
              <p className={`mb-4 ${P}`}>
                例示の二分探索 (20 行) と、同じ PDF にある Python3 の記述例を並べました。Python は、入力の行を「問いの文の表示」と「値 52」の 2 行に替え、空白と字下げ (空白 4 つ) を整えた物です。「実行」を押すとブラウザの中で Python が動きます。atai を 85 に替えると、見つからなかったときの道を通ります。
              </p>
              <ExampleBlock example={BINARY_SEARCH} />
              <p className={`mt-4 ${P}`}>
                例示の実行結果の表示例は「52 は 5 番目にありました」で、上の出力と同じです。表示例にある「←キーボードから入力」は説明のための書き込みで、プログラムが出す字ではありません。
              </p>
              <h3 className="mb-3 mt-8 text-[20px] font-bold">割り算の 3 つとべき乗</h3>
              <p className={`mb-4 ${P}`}>
                7 / 2、7 ÷ 2、10 ％ 3 は、旧 DNCL の説明書が例に使っている数です。説明書の答えは 3.5、3、1 で、Python でも同じになります。
              </p>
              <ExampleBlock example={OPERATORS} />
            </div>

            <div className="mt-12">
              <SectionTitle id="drill">書き換えを自分で試す</SectionTitle>
              <p className={`mb-4 ${P}`}>
                情報 I の表記を読んで Python に書き換え、「実行」を押してください。出力が期待の出力と同じなら、欄の下に「一致しました」と出ます。行の終わりの空白は比べません。
              </p>
              <div className="flex flex-col gap-6">
                {DRILLS.map((d) => (
                  <DrillCard key={d.id} drill={d} />
                ))}
              </div>
            </div>

            <div className="mt-12">
              <SectionTitle id="pitfalls">書き間違いと読み間違い</SectionTitle>
              <div className="flex flex-col gap-4">
                {PITFALLS.map((p) => (
                  <section key={p.title} className="rounded-md border border-main bg-surface p-5">
                    <h3 className="m-0 text-[19px] font-bold">{p.title}</h3>
                    <p className={`mt-2 ${P}`}>{p.body}</p>
                    {p.wrong && p.right ? (
                      <div className="mt-3 grid gap-3 md:grid-cols-2">
                        <NotationCode code={p.wrong} label="間違った書き方" />
                        <NotationCode code={p.right} label="直した書き方" />
                      </div>
                    ) : null}
                  </section>
                ))}
              </div>
            </div>

            <div className="mt-12">
              <SectionTitle id="sources">出典と確かめた日</SectionTitle>
              <p className={`mb-4 ${P}`}>
                {`この頁は、情報 I の表記を例示 (2022 年 11 月 9 日) に基づいて書いています。本試験は問題文の説明に従ってください。下の 4 つの PDF は ${jaDate(PAGE_META.checked)} に大学入試センターから取り直し、表の 1 行ずつと照らしました。`}
              </p>
              <p className={`mb-4 ${P}`}>
                令和 7 年度と令和 8 年度の本試験の情報 I のプログラムも、代入の =、表示する、「1 ずつ増やしながら繰り返す」、行の頭の線で範囲を表す書き方で、例示と同じ形でした。違ったのは添字で、どちらの年も問題文で、配列の添字は 1 から始まると断っていました。令和 8 年度の問題では、使う関数 (要素数、最大値) を問題文の【関数の説明】の枠で説明しています。
              </p>
              <ul className="flex flex-col gap-3 pl-0">
                {SOURCES.map((s) => (
                  <li key={s.id} className="list-none rounded-md border border-main bg-surface p-4">
                    <a href={s.url} className="text-[17px] font-bold text-primary underline underline-offset-4 [overflow-wrap:anywhere]" rel="noopener" target="_blank">
                      {s.title}
                    </a>
                    <p className="mt-1 text-[16px] leading-[1.8] text-sub">{s.note}</p>
                    <p className="mt-1 font-mono text-[15px] text-sub [overflow-wrap:anywhere]">{s.url}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-12">
              <SectionTitle id="next">次に学ぶ</SectionTitle>
              <div className="grid gap-4 md:grid-cols-3">
                {NEXT_LINKS.map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    className="block rounded-md border border-main bg-surface p-5 no-underline transition-colors hover:border-dark focus-visible:outline-2 focus-visible:outline-offset-2"
                  >
                    <span className="block text-[18px] font-bold text-primary">{n.title}</span>
                    <span className="mt-2 block text-[16px] leading-[1.8] text-main">{n.body}</span>
                  </Link>
                ))}
              </div>
            </div>
          </article>
        </div>
      </div>
    </PublicPageShell>
  );
}
